import { BedrockAgentCoreClient, InvokeAgentRuntimeCommand } from '@aws-sdk/client-bedrock-agentcore';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { awsConfig } from '../aws-config';
import { refreshTokenIfNeeded } from '../utils/tokenRefresh';

const BACKEND_MODE = (window as any).MEPPY_BACKEND || 'meppy-agentcore';

const AGENT_ARNS: Record<string, string> = {
  'meppy-agentcore': 'arn:aws:bedrock-agentcore:us-west-2:090600307342:runtime/meppy_research_agent-CPbnrS99aj',
  'supermeppy-agentcore': 'arn:aws:bedrock-agentcore:us-west-2:090600307342:runtime/supermeppy_research_agent-OVnd9dD4er'
};

const AGENT_ARN = AGENT_ARNS[BACKEND_MODE];

export class AgentCoreService {
  private client!: BedrockAgentCoreClient;
  private token: string;
  private sessionId: string;

  constructor(token: string) {
    this.token = token;
    this.sessionId = this.generateSessionId();
    this.initializeClient(token);
  }

  private initializeClient(token: string) {
    this.client = new BedrockAgentCoreClient({
      region: awsConfig.region,
      credentials: fromCognitoIdentityPool({
        clientConfig: { region: awsConfig.region },
        identityPoolId: awsConfig.identityPoolId,
        logins: {
          [`cognito-idp.${awsConfig.region}.amazonaws.com/${awsConfig.userPoolId}`]: token
        }
      })
    });
  }

  async sendMessage(message: string): Promise<ReadableStream> {
    // Refresh token if needed
    const freshToken = await refreshTokenIfNeeded();
    if (freshToken && freshToken !== this.token) {
      this.token = freshToken;
      this.initializeClient(freshToken);
    }
    
    const traceId = this.generateTraceId();
    
    try {
      const payload = JSON.stringify({ 
        prompt: message,
        user_email: this.extractEmailFromToken()
      });
      
      const command = new InvokeAgentRuntimeCommand({
        agentRuntimeArn: AGENT_ARN,
        runtimeSessionId: this.sessionId,
        payload: new TextEncoder().encode(payload),
        // Add observability headers for CloudWatch tracking
        traceId: traceId
      });

      // Log session start for debugging
      console.log(`[AgentCore] Session: ${this.sessionId}, Trace: ${traceId}, User: ${this.extractEmailFromToken()}`);
      
      const response = await this.client.send(command);
      
      // AgentCore returns AsyncIterable stream
      if (response.response) {
        const asyncIterable = response.response as AsyncIterable<Uint8Array>;
        const sessionId = this.sessionId;
        
        return new ReadableStream({
          async start(controller) {
            try {
              const decoder = new TextDecoder();
              
              for await (const chunk of asyncIterable) {
                // Decode chunk and forward as-is (already in SSE format from backend)
                const text = decoder.decode(chunk, { stream: true });
                controller.enqueue(new TextEncoder().encode(text));
              }
              
              controller.close();
            } catch (error) {
              console.error(`[AgentCore] Stream error for session ${sessionId}:`, error);
              controller.error(error);
            }
          }
        });
      }
      
      // Fallback if streaming fails
      const sessionId = this.sessionId;
      return new ReadableStream({
        start(controller) {
          controller.enqueue(new TextEncoder().encode(
            `data: ${JSON.stringify({
              type: "error", 
              error: "AgentCore streaming not available", 
              session_id: sessionId
            })}\n\n`
          ));
          controller.close();
        }
      });
      
    } catch (error: any) {
      const sessionId = this.sessionId;
      return new ReadableStream({
        start(controller) {
          controller.enqueue(new TextEncoder().encode(
            `data: ${JSON.stringify({
              type: "error", 
              error: `AgentCore error: ${error?.message || 'Unknown error'}`, 
              session_id: sessionId
            })}\n\n`
          ));
          controller.close();
        }
      });
    }
  }

  async getSessions(): Promise<any> {
    return { active_sessions: [] };
  }

  async getHealth(): Promise<any> {
    return { status: "healthy", backend: BACKEND_MODE };
  }

  private generateSessionId(): string {
    const timestamp = Date.now().toString();
    const random = Math.random().toString(36).substr(2, 15);
    return `${BACKEND_MODE}-session-${timestamp}-${random}`;
  }

  private generateTraceId(): string {
    return `trace-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private extractEmailFromToken(): string {
    try {
      const base64Url = this.token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      const decoded = JSON.parse(jsonPayload);
      return decoded.email || decoded.username || 'unknown@example.com';
    } catch (error) {
      return 'unknown@example.com';
    }
  }
}
